import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, Lock, CheckCircle2 } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error('Senha deve ter no mínimo 6 caracteres'); return; }
    if (password !== confirmPassword) { toast.error('As senhas não coincidem'); return; }
    if (!token) { toast.error('Token inválido'); return; }

    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
      toast.success('Senha redefinida com sucesso!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao redefinir senha');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8" style={{ background: 'linear-gradient(180deg, #0a0a1a 0%, #111133 50%, #0a0a1a 100%)' }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">U-Clean</h1>
        </div>

        <Card className="border-white/10 bg-white/5 backdrop-blur-md shadow-2xl">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Lock className="h-5 w-5 text-purple-400" /> Redefinir Senha
            </CardTitle>
            <CardDescription className="text-gray-400">
              {success ? 'Senha redefinida com sucesso!' : 'Crie uma nova senha para sua conta'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {success ? (
              <div className="text-center space-y-4" data-testid="reset-success">
                <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto">
                  <CheckCircle2 className="h-8 w-8 text-emerald-400" />
                </div>
                <p className="text-sm text-gray-300">Sua senha foi redefinida. Você já pode fazer login com a nova senha.</p>
                <Button onClick={() => navigate('/login')} className="bg-purple-600 hover:bg-purple-700" data-testid="go-to-login-btn">
                  Ir para o login
                </Button>
              </div>
            ) : !token ? (
              <div className="text-center space-y-4" data-testid="reset-no-token">
                <p className="text-sm text-red-400">Link inválido ou expirado.</p>
                <Link to="/esqueci-senha">
                  <Button variant="outline" className="border-white/10 text-gray-300">Solicitar novo link</Button>
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-password-form">
                <div>
                  <Label htmlFor="new-password" className="text-gray-300">Nova senha</Label>
                  <Input
                    id="new-password" type="password" placeholder="Mínimo 6 caracteres"
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    required minLength={6}
                    className="bg-white/5 border-white/10 text-white placeholder:text-gray-600"
                    data-testid="new-password-input"
                  />
                </div>
                <div>
                  <Label htmlFor="confirm-password" className="text-gray-300">Confirmar nova senha</Label>
                  <Input
                    id="confirm-password" type="password" placeholder="Repita a senha"
                    value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                    required minLength={6}
                    className="bg-white/5 border-white/10 text-white placeholder:text-gray-600"
                    data-testid="confirm-password-input"
                  />
                </div>
                <Button type="submit" className="w-full bg-purple-600 hover:bg-purple-700 text-white" disabled={loading} data-testid="reset-submit-btn">
                  {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Redefinir Senha
                </Button>
                <div className="text-center">
                  <Link to="/login" className="text-sm text-purple-400 hover:underline">
                    <ArrowLeft className="h-3 w-3 inline mr-1" /> Voltar ao login
                  </Link>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
